import { create } from 'zustand';

import categoryService, { type Category as ServiceCategory } from '../services/category.service';

import type { CreateCategoryData, UpdateCategoryData } from '../services/category.service';

// Use the Category type from the service to ensure type consistency
export type Category = ServiceCategory & {
    // Extended properties for UI usage
    questionCount?: number;
    examCount?: number;
};

interface CategoryState {
    categories: Category[];
    loading: boolean;
    error: string | null;

    fetchCategories: () => Promise<void>;
    createCategory: (data: CreateCategoryData) => Promise<void>;
    updateCategory: (id: string, data: UpdateCategoryData) => Promise<void>;
    deleteCategory: (id: string) => Promise<void>;
    setCategories: (categories: Category[]) => void;
    clearError: () => void;
}

export const useCategoryStore = create<CategoryState>((set, get) => ({
    categories: [],
    loading: false,
    error: null,

    fetchCategories: async () => {
        set({ loading: true, error: null });
        try {
            const response = await categoryService.listCategories();
            set({ categories: response.categories, loading: false });
        } catch (error: any) {
            set({ error: error.message || 'Failed to fetch categories', loading: false });
        }
    },

    createCategory: async (data: CreateCategoryData) => {
        set({ loading: true, error: null });
        try {
            await categoryService.createCategory(data);
            await get().fetchCategories();
        } catch (error: any) {
            set({ error: error.message || 'Failed to create category', loading: false });
            throw error;
        }
    },

    updateCategory: async (id: string, data: UpdateCategoryData) => {
        set({ loading: true, error: null });
        try {
            await categoryService.updateCategory(id, data);
            await get().fetchCategories();
        } catch (error: any) {
            set({ error: error.message || 'Failed to update category', loading: false });
            throw error;
        }
    },

    deleteCategory: async (id: string) => {
        set({ loading: true, error: null });
        try {
            await categoryService.deleteCategory(id);
            await get().fetchCategories();
        } catch (error: any) {
            set({ error: error.message || 'Failed to delete category', loading: false });
            throw error;
        }
    },

    setCategories: (categories) => {
        set({ categories });
    },

    clearError: () => {
        set({ error: null });
    },
}));
